import React, { useCallback } from 'react';
import _ from 'lodash';
import { connect as connectR } from 'react-redux';
import { Link } from 'react-router';
import Card from '../main/card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { faAddressCard } from '@fortawesome/free-regular-svg-icons';
import { RootState } from '../../store';
import { ManagedPartnerDto } from '../../store/partnerManagerSlice';
import { Mansion } from '../../store/mansionsSlice';
import { LocationOverviewDto, MansionById } from '../../store/locationsSlice';
import { UsageByYearMonth } from '../../store/usageDataSlice';
import { Appliance } from '../../store/applianceSlice';
import useTranslate from '../useTranslate';
import useFeatureAccess from '../../util/useFeatureAccess';
import { VatIdVerificationStatus } from '../../store/userDetailsSlice';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';

type SelectionOptionsType =
    | 'profile'
    | 'dashboard'
    | 'objects'
    | 'turnover'
    | 'partner-invoices'
    | 'downloads'
    | 'roles'
    | 'operational-contacts'
    | 'usage-data';

interface Props {
    selectedPartner: ManagedPartnerDto;
    bankDataAvailable: boolean | null;
    vatinVerification: VatIdVerificationStatus | null;
    selected: SelectionOptionsType;
    locations?: MansionById;
    objects?: { [key: string]: Mansion };
    appliances?: { [applianceId: number]: Appliance };
    selectedObject?: number;
    selectedLocation?: number;
    selectedAppliance?: number;
    usageDataByYearMonth?: UsageByYearMonth;
    usageDataSelected?: { year: number; month: number };
    mansionsWithMissingContact: number[] | null;
}

function VerticalNavigation(props: Readonly<Props>) {
    const strings = useTranslate('VerticalNavigation');
    const { canReadFeature, canWriteFeature } = useFeatureAccess({
        feature_access: props.selectedPartner?.feature_access,
    });

    const hasWriteSettlement = canWriteFeature('MASTER_SETTLEMENT_DETAILS');
    const shouldChangeBankData = hasWriteSettlement && !props.bankDataAvailable;

    const hasWriteContractData = canWriteFeature('MASTER_CONTRACT_DATA');
    const shouldChangeVatin =
        hasWriteContractData && props.vatinVerification === 'INVALID';

    const showDangerOnProfile = shouldChangeBankData || shouldChangeVatin;

    const hasWriteOperationalContacts = canWriteFeature(
        'GENERAL_OPERATIONAL_CONTACTS'
    );
    const mansionIdsWithWarning = hasWriteOperationalContacts
        ? props.mansionsWithMissingContact || []
        : [];
    const showMansionWarning = mansionIdsWithWarning.length > 0;

    const showPartnerInvoices =
        props.selectedPartner?.partner_invoices_available &&
        canReadFeature('ACCOUNTING_PARTNER_INVOICES');

    const buildObjectRows = useCallback(() => {
        function buildApplianceRows(
            location: LocationOverviewDto,
            rows: JSX.Element[],
            object: Mansion
        ) {
            if (props.selectedLocation === location.id && props.appliances) {
                _.map(props.appliances, (appliance) => {
                    let classname = 'card-footer-item';
                    if (props.selectedAppliance === appliance.id)
                        classname += ' selected';

                    let icon: string;
                    if (appliance.appliance_type === 'DRYER') {
                        icon = 'icon-wew-dryer-large';
                    } else {
                        icon = 'icon-wew-washmachine-large';
                    }

                    rows.push(
                        <div
                            key={'appliance_' + appliance.id}
                            className="card-footer-item-wrapper"
                            style={{ marginLeft: '40px' }}
                        >
                            <Link
                                to={
                                    '/objects/' +
                                    object.id +
                                    '/' +
                                    location.id +
                                    '/' +
                                    appliance.id
                                }
                                className={classname}
                                data-testid={'appliance-entry_' + appliance.id}
                            >
                                <span className="card-footer-icon">
                                    <i className={icon} />
                                </span>
                                <span className="title">
                                    {appliance.short_name}
                                </span>
                            </Link>
                        </div>
                    );
                });
            }
        }

        function buildLocationRows(object: Mansion, rows: JSX.Element[]) {
            if (props.locations)
                _.map(props.locations[object.id], (location) => {
                    let classname = 'card-footer-item';
                    if (props.selectedLocation === location.id)
                        classname += ' selected';
                    rows.push(
                        <div
                            key={'location_' + location.id}
                            className="card-footer-item-wrapper"
                            style={{ marginLeft: '25px' }}
                        >
                            <Link
                                to={'/objects/' + object.id + '/' + location.id}
                                className={classname}
                                data-testid={'location-entry_' + location.id}
                            >
                                <span className="card-footer-icon">
                                    <i className="icon-wew-washroom" />
                                </span>
                                <span className="title">{location.name}</span>
                            </Link>
                        </div>
                    );
                    if (canReadFeature('RESIDENTIAL_APPLIANCE')) {
                        buildApplianceRows(location, rows, object);
                    }
                });
        }

        return (
            props.objects &&
            _.map(props.objects, (object) => {
                const isSelected = props.selectedObject === object.id;
                const hasWarning = mansionIdsWithWarning.includes(object.id);
                const classname = isSelected
                    ? 'card-footer-item selected'
                    : 'card-footer-item';
                const rows = [
                    <div
                        key={'object_' + object.id}
                        className="card-footer-item-wrapper"
                        style={{ paddingLeft: '10px' }}
                    >
                        <Link
                            to={'/objects/' + object.id}
                            className={classname}
                            data-testid={'object-entry_' + object.id}
                        >
                            <span className="card-footer-icon">
                                <i className="fa fa-home fa-lg" />
                            </span>
                            <span className="title">
                                {object.address.street +
                                    ' ' +
                                    object.address.housenumber +
                                    ' '}
                                {hasWarning && (
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        size={'sm'}
                                        color={'#FF4343'}
                                    />
                                )}
                            </span>
                        </Link>
                    </div>,
                ];

                if (isSelected && canReadFeature('RESIDENTIAL_LOCATION')) {
                    buildLocationRows(object, rows);
                }

                return rows;
            })
        );
    }, [
        props.selectedObject,
        props.selectedLocation,
        props.selectedAppliance,
        props.locations,
        props.objects,
        props.appliances,
        mansionIdsWithWarning,
    ]);

    const buildPartnerRow = useCallback(() => {
        const selectedPartner = props.selectedPartner;
        if (!selectedPartner) {
            return null;
        }
        return (
            <div key="partner-row" className="mt-20">
                <div>{selectedPartner.company_name}</div>
                <div>{selectedPartner.name}</div>
                <div>{`${strings.customerId} ${selectedPartner.customer_id}`}</div>
                {selectedPartner.city && (
                    <div>
                        {selectedPartner.street_and_housenumber},{' '}
                        {selectedPartner.city}
                    </div>
                )}
            </div>
        );
    }, [props.selectedPartner]);

    const buildUsageDataRows = useCallback(() => {
        const usageDataClassname = `card-footer-item${props.selected === 'usage-data' ? ' selected' : ''}`;
        const dateFormatter = new Intl.DateTimeFormat('default', {
            year: 'numeric',
            month: 'short',
        });

        const usageMonthList = [];
        let selectedYear = 0;
        let selectedMonth = 0;
        if (props.usageDataSelected) {
            const { month, year } = props.usageDataSelected;
            const availableMonths = Object.keys(
                props.usageDataByYearMonth[year] || {}
            );
            availableMonths.sort((a, b) => Number(b) - Number(a));
            usageMonthList.push(...availableMonths);
            selectedMonth = Number(month);
            selectedYear = Number(year);
        }

        return [
            <div key={5} className={'card-footer-item-wrapper'}>
                <Link
                    to={'/usage-data'}
                    className={usageDataClassname}
                    data-testid="usage-data-entry"
                >
                    <span className={'card-footer-icon'}>
                        <i className={'fa fa-bar-chart fa-lg'} />
                    </span>
                    <span className={'title'}>{strings.usageData}</span>
                </Link>
            </div>,
            ...usageMonthList.map((month) => (
                <div
                    key={`usage-report-${selectedYear * 100 + Number(month)}`}
                    className={'card-footer-item-wrapper'}
                    style={{ marginLeft: '20px' }}
                >
                    <Link
                        to={`/usage-data/${selectedYear * 100 + Number(month)}`}
                        className={`card-footer-item ${Number(month) === selectedMonth ? 'selected' : ''}`}
                    >
                        <span className={'card-footer-icon'}>
                            <i className={'fa fa-list-alt fa-lg'} />
                        </span>
                        <span className={'title'}>
                            {renderYearAndMonth(selectedYear, month)}
                        </span>
                    </Link>
                </div>
            )),
        ];

        function renderYearAndMonth(year: number, month: number) {
            return dateFormatter.format(new Date(year, month - 1));
        }
    }, [props.selected, props.usageDataByYearMonth, props.usageDataSelected]);

    let profileClassname = 'card-footer-item';
    let dashboardClassname = 'card-footer-item';
    let objectsClassname = 'card-footer-item';
    let turnoverClassname = 'card-footer-item';
    let downloadsClassname = 'card-footer-item';
    let rolesClassName = 'card-footer-item';
    let partnerInvoiceClassname = 'card-footer-item';
    let operationalContactsClassname = 'card-footer-item';
    switch (props.selected) {
        case 'profile':
            profileClassname += ' selected';
            break;
        case 'dashboard':
            dashboardClassname += ' selected';
            break;
        case 'objects':
            objectsClassname += ' selected';
            break;
        case 'turnover':
            turnoverClassname += ' selected';
            break;
        case 'downloads':
            downloadsClassname += ' selected';
            break;
        case 'roles':
            rolesClassName += ' selected';
            break;
        case 'partner-invoices':
            partnerInvoiceClassname += ' selected';
            break;
        case 'operational-contacts':
            operationalContactsClassname += ' selected';
            break;
        default:
            break;
    }

    return (
        <Card
            title={[strings.myTitle, strings.weWash]}
            type="card-primary"
            className="card-no-min-height"
            data-testid="vertical-navigation"
            footerItems={[
                <div key={4} className="card-footer-item-wrapper">
                    <Link
                        to="/dashboard"
                        className={dashboardClassname}
                        data-testid="dashboard-entry"
                    >
                        <span className="card-footer-icon">
                            <i className="fa fa-dashboard fa-lg" />
                        </span>
                        <span className="title">{strings.dashboard}</span>
                    </Link>
                </div>,
                canReadFeature('MASTER_CONTRACT_DATA') && (
                    <div key={3} className="card-footer-item-wrapper">
                        <Link
                            to="/profile"
                            className={profileClassname}
                            data-testid="profile-entry"
                        >
                            <span className="icon-wew-admin-id icon-sm card-footer-icon" />
                            <span className="title">
                                {strings.editPartnerData}{' '}
                                {showDangerOnProfile && (
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        size={'sm'}
                                        color={'#FF4343'}
                                    />
                                )}
                            </span>
                        </Link>
                    </div>
                ),
                canReadFeature('GENERAL_ACCESS_MANAGEMENT') && (
                    <div key={'roles'} className="card-footer-item-wrapper">
                        <Link
                            to="/roles"
                            className={rolesClassName}
                            data-testid="roles-entry"
                        >
                            <span className="card-footer-icon">
                                <FontAwesomeIcon icon={faUserPlus} />
                            </span>
                            <span className="title">{strings.roles}</span>
                        </Link>
                    </div>
                ),
                canReadFeature('GENERAL_OPERATIONAL_CONTACTS') && (
                    <div
                        key={'operational-contacts'}
                        className="card-footer-item-wrapper"
                    >
                        <Link
                            to="/operational-contacts"
                            className={operationalContactsClassname}
                            data-testid="operational-contacts-entry"
                        >
                            <span className="card-footer-icon">
                                <FontAwesomeIcon icon={faAddressCard} />
                            </span>
                            <span className="title">
                                {strings.operationalContacts}
                            </span>
                        </Link>
                    </div>
                ),
                canReadFeature('ACCOUNTING_CREDIT_NOTES') && (
                    <div key={2} className="card-footer-item-wrapper">
                        <Link
                            to="/turnover"
                            className={turnoverClassname}
                            data-testid="turnover-entry"
                        >
                            <span className="card-footer-icon">
                                <i className="fa fa-file-text-o fa-lg" />
                            </span>
                            <span className="title">{strings.payment}</span>
                        </Link>
                    </div>
                ),
                canReadFeature('ACCOUNTING_NUMBERS') && buildUsageDataRows(),
                showPartnerInvoices && (
                    <div
                        key={'partner-invoices'}
                        className={'card-footer-item-wrapper'}
                    >
                        <Link
                            to={'/partner-invoices'}
                            className={partnerInvoiceClassname}
                            data-testid="partner-invoices-entry"
                        >
                            <span className="card-footer-icon">
                                <FontAwesomeIcon icon={faFileInvoice} />
                            </span>
                            <span className="title">
                                {strings.partnerInvoices}
                            </span>
                        </Link>
                    </div>
                ),
                <div key={1} className="card-footer-item-wrapper">
                    <Link
                        to="/downloads"
                        className={downloadsClassname}
                        data-testid="downloads-entry"
                    >
                        <span className="card-footer-icon">
                            <i className="fa fa-download fa-lg" />
                        </span>
                        <span className="title">{strings.downloads}</span>
                    </Link>
                </div>,
                canReadFeature('RESIDENTIAL_MANSION') && (
                    <div key={0} className="card-footer-item-wrapper">
                        <Link
                            to="/objects"
                            className={objectsClassname}
                            data-testid="objects-entry"
                        >
                            <span className="card-footer-icon">
                                <i className="fa fa-home fa-lg" />
                            </span>
                            <span className="title">
                                {strings.objects}{' '}
                                {showMansionWarning && (
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        size={'sm'}
                                        color={'#FF4343'}
                                    />
                                )}
                            </span>
                        </Link>
                    </div>
                ),
                canReadFeature('RESIDENTIAL_MANSION') && buildObjectRows(),
            ]}
        >
            {buildPartnerRow()}
        </Card>
    );
}

export default connectR((store: RootState) => ({
    selectedPartner: store.partnerManager.selectedPartner,
    bankDataAvailable:
        store.userDetails.settlementBillingDetails?.bank_data_available,
    vatinVerification:
        store.userDetails.contractDetails?.vat_id_verification_status,
    mansionsWithMissingContact:
        store.operationalContacts.operationalContacts
            ?.mansions_with_missing_contact,
}))(VerticalNavigation);
